import { pick, type Rng } from './rng.ts';
import type { Locale } from './types.ts';

// Local stand-in for the "Aide-moi à inventer" button: typing on a phone is slow, so a
// ready-made false rumor gets the writer unstuck. Bots use it too.

interface Lexicon {
  templates: string[];
  action: string[];
  place: string[];
  secret: string[];
  collection: string[];
  skill: string[];
  deed: string[];
  event: string[];
  celebrity: string[];
  subtitles: string[];
}

const LEXICONS: Record<Locale, Lexicon> = {
  fr: {
    templates: [
      'On a vu {name} {action} {place}',
      '{name} aurait avoué {secret}',
      '{name} collectionnerait en secret {collection}',
      '{name} prendrait des cours de {skill} en cachette',
      '{name} aurait {deed} pendant {event}',
      '{name} entretiendrait une correspondance passionnée avec {celebrity}',
      'Nos sources ont surpris {name} {action} {place}',
    ],
    action: [
      'en train de danser la macarena',
      'en train de pleurer devant une pub pour du yaourt',
      'en train de parler à ses plantes',
      'en train de chanter du Céline Dion à tue-tête',
      "en train de négocier le prix d'une baguette",
      'en train de faire un selfie avec un pigeon',
      'en train de répéter son discours des Oscars',
      'en train de promener un chat en laisse',
      "en train de manger une raclette en plein mois d'août",
      "en train d'applaudir l'atterrissage d'un avion",
    ],
    place: [
      'dans un parking à 3 h du matin',
      'au rayon surgelés',
      'dans un karaoké de Pigalle',
      "au fond d'un bus de nuit",
      "dans les toilettes d'un mariage",
      "à la sortie d'un cours de salsa",
      'sur un pédalo en novembre',
      'dans un magasin de déguisements',
      'devant la mairie',
      'à une brocante de village',
    ],
    secret: [
      'avoir un compte fan dédié à son chat',
      'ne jamais avoir vu Titanic en entier',
      'connaître par cœur toutes les chorégraphies des 2Be3',
      'avoir menti sur son âge pendant trois ans',
      'avoir peur des papillons',
      'avoir mangé des croquettes pour chien par erreur',
      'dormir avec une veilleuse en forme de dauphin',
      'avoir raté son code de la route quatre fois',
      'avoir écrit une lettre au Père Noël à 25 ans',
      'pleurer à chaque finale de concours de cuisine',
    ],
    collection: [
      'des tickets de caisse',
      'des photos de nuages',
      'des nains de jardin',
      "des sachets de sucre d'hôtel",
      'des bouchons de liège',
      'des cartes Pokémon brillantes',
      'des magnets de stations-service',
      'des dents de lait (pas les siennes)',
    ],
    skill: ['claquettes', 'yodel', 'tricot de compétition', 'ventriloquie', 'danse du ventre', 'cornemuse', 'lancer de hache', 'mime'],
    deed: [
      'fait un discours en pleurant',
      'dansé sur la table',
      "demandé quelqu'un en mariage",
      'confondu son patron avec un serveur',
      'oublié son propre prénom',
      'appelé sa prof « maman »',
      'volé la vedette aux mariés',
      'fait un malaise en voyant un canard',
    ],
    event: [
      'un enterrement de vie de célibataire',
      'un repas de famille',
      "un entretien d'embauche",
      'un match de foot',
      'une réunion en visio',
      "une fête d'anniversaire pour enfants",
      'un vernissage',
      'le réveillon de Noël',
    ],
    celebrity: [
      'une star de la chanson',
      'un candidat de télé-réalité',
      'son ancien prof de SVT',
      'la voix du GPS',
      'un ex-chanteur de boys band',
      'la présentatrice météo',
      'un moine tibétain',
      'le boulanger du quartier',
    ],
    subtitles: [
      'Nos sources sont formelles.',
      'Un témoin affirme avoir tout vu.',
      'Son entourage dément formellement.',
      "L'affaire fait grand bruit dans le quartier.",
      'La rédaction a pu consulter des preuves accablantes.',
      "Personne n'a souhaité commenter.",
    ],
  },
  en: {
    templates: [
      '{name} was spotted {action} {place}',
      '{name} has reportedly confessed to {secret}',
      '{name} secretly collects {collection}',
      '{name} is said to be taking {skill} lessons in secret',
      '{name} allegedly {deed} during {event}',
      '{name} is rumored to be exchanging passionate letters with {celebrity}',
      'Our sources caught {name} {action} {place}',
    ],
    action: [
      'doing the Macarena',
      'crying at a yogurt commercial',
      'talking to their houseplants',
      'belting out Céline Dion',
      'haggling over the price of a baguette',
      'taking a selfie with a pigeon',
      'rehearsing an Oscars speech',
      'walking a cat on a leash',
      'eating fondue in the middle of August',
      'applauding when the plane landed',
    ],
    place: [
      'in a parking lot at 3 a.m.',
      'in the frozen food aisle',
      'in a karaoke bar downtown',
      'at the back of a night bus',
      'in the restroom at a wedding',
      'leaving a salsa class',
      'on a pedal boat in November',
      'in a costume shop',
      'outside city hall',
      'at a village flea market',
    ],
    secret: [
      'running a fan account for their cat',
      'never having watched Titanic all the way through',
      'knowing every boy band dance routine by heart',
      'lying about their age for three years',
      'being terrified of butterflies',
      'eating dog food by mistake',
      'sleeping with a dolphin night-light',
      'failing their driving test four times',
      'writing to Santa at the age of 25',
      'crying at every cooking show finale',
    ],
    collection: [
      'grocery receipts',
      'photos of clouds',
      'garden gnomes',
      'hotel sugar packets',
      'wine corks',
      'shiny Pokémon cards',
      'gas station fridge magnets',
      'baby teeth (not their own)',
    ],
    skill: ['tap dance', 'yodeling', 'competitive knitting', 'ventriloquism', 'belly dance', 'bagpipe', 'axe throwing', 'mime'],
    deed: [
      'gave a speech in tears',
      'danced on the table',
      'proposed to someone',
      'mistook their boss for a waiter',
      'forgot their own name',
      'called their teacher "Mom"',
      'upstaged the newlyweds',
      'fainted at the sight of a duck',
    ],
    event: [
      'a bachelor party',
      'a family dinner',
      'a job interview',
      'a football match',
      'a video call',
      "a kid's birthday party",
      'an art opening',
      'Christmas Eve dinner',
    ],
    celebrity: [
      'a pop star',
      'a reality TV contestant',
      'their old biology teacher',
      'the GPS voice',
      'a former boy band singer',
      'the TV weather presenter',
      'a Tibetan monk',
      'the local baker',
    ],
    subtitles: [
      'Our sources are adamant.',
      'A witness claims to have seen everything.',
      'Those close to them firmly deny it.',
      'The whole neighborhood is talking.',
      'The newsroom has seen damning evidence.',
      'Nobody wished to comment.',
    ],
  },
};

type Slot = Exclude<keyof Lexicon, 'templates' | 'subtitles'>;

export function inventRumor(locale: Locale, name: string, rng: Rng = Math.random): { headline: string; subtitle: string } {
  const lexicon = LEXICONS[locale];
  const headline = pick(rng, lexicon.templates).replace(/\{(\w+)\}/g, (_, slot: string) =>
    slot === 'name' ? name : pick(rng, lexicon[slot as Slot]),
  );
  return { headline, subtitle: pick(rng, lexicon.subtitles) };
}

export function subtitleSuggestions(locale: Locale): readonly string[] {
  return LEXICONS[locale].subtitles;
}

/** Fill-in-the-blank headlines; `___` marks where the writer's cursor lands. */
export const BLANK_TEMPLATES: Record<Locale, readonly string[]> = {
  fr: [
    'On a vu {name} en train de ___',
    '{name} aurait secrètement ___',
    'Selon nos sources, {name} ___',
    '{name} jure que ___, mais nos sources affirment le contraire',
    "{name} ne voudrait surtout pas que l'on sache ___",
  ],
  en: [
    '{name} was spotted ___',
    '{name} has secretly been ___',
    'According to our sources, {name} ___',
    '{name} swears ___, but our sources say otherwise',
    '{name} would rather nobody found out ___',
  ],
};

export const BLANK = '___';

export function fillName(template: string, name: string): string {
  return template.replaceAll('{name}', name);
}
